// Persisted visibility for the opt-in Service Overview cards. Kept in
// localStorage so the choice survives reloads without a server round-trip.
// Default = all off, so the tab issues no extra vendor requests until a user
// explicitly enables a card via the "Display options" menu.
import { useCallback, useState } from 'react';

export type OptionalCardKey = 'topPaths' | 'topDomains';
export type OptionalCards = Record<OptionalCardKey, boolean>;

const STORAGE_KEY = 'cdn.serviceOverview.optionalCards';
const DEFAULTS: OptionalCards = { topPaths: false, topDomains: false };

function load(): OptionalCards {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return raw ? { ...DEFAULTS, ...(JSON.parse(raw) as Partial<OptionalCards>) } : DEFAULTS;
  } catch {
    return DEFAULTS;
  }
}

/** Read + toggle the optional-card visibility set, persisting each change. */
export function useOptionalCards() {
  const [cards, setCards] = useState<OptionalCards>(load);

  const toggle = useCallback((key: OptionalCardKey, on: boolean) => {
    setCards((prev) => {
      const next = { ...prev, [key]: on };
      try {
        localStorage.setItem(STORAGE_KEY, JSON.stringify(next));
      } catch {
        /* ignore quota / disabled storage */
      }
      return next;
    });
  }, []);

  return { cards, toggle };
}
